// ─── the tools this client owns ──────────────────────────────────────────────
// A voice app is the one caller that can do things no server can: change what is on
// screen. These are declared to the session as `exec: { kind: 'client' }`, so the
// model can ask for them and the session routes them back down as
// `tool.call` / `runAt: 'client'` — the third seam, visible in one glance.
//
// `deep_think` is declared too, as the counter-example: a builtin the session runs
// on a stronger model while the voice keeps talking. Fast mouth, slow brain.

import type { ToolRequest } from '@voicebox/client'
import { FACE_EMOTIONS, FACE_IDS } from '@voicebox/face'

export interface FaceControls {
  setFace(id: string): Promise<void>
  setEmotion(emotion: string): void
  currentFace(): string
}

export const CLIENT_TOOL_DECLARATIONS: ToolRequest[] = [
  { name: 'deep_think', exec: { kind: 'builtin' } },
  {
    name: 'set_face',
    description: `Change which face the user sees. One of: ${FACE_IDS.join(', ')}.`,
    parameters: {
      type: 'object',
      properties: { face: { type: 'string', enum: [...FACE_IDS], description: 'The face to show.' } },
      required: ['face'],
    },
    exec: { kind: 'client' },
    timeoutMs: 8000,
  },
  {
    name: 'set_emotion',
    description: `Set the expression on your own face. One of: ${FACE_EMOTIONS.join(', ')}.`,
    parameters: {
      type: 'object',
      properties: { emotion: { type: 'string', enum: [...FACE_EMOTIONS], description: 'How you look right now.' } },
      required: ['emotion'],
    },
    exec: { kind: 'client' },
    timeoutMs: 4000,
  },
]

/** Handlers for the declarations above, in the shape `ToolBridge` dispatches. */
export function clientToolHandlers(face: FaceControls): Record<string, (args: unknown) => Promise<unknown> | unknown> {
  return {
    async set_face(args) {
      const id = String(pick(args, 'face') ?? '')
      if (!FACE_IDS.includes(id as (typeof FACE_IDS)[number])) throw new Error(`no such face "${id}" — have ${FACE_IDS.join(', ')}`)
      await face.setFace(id)
      return { face: face.currentFace() }
    },
    set_emotion(args) {
      const emotion = String(pick(args, 'emotion') ?? 'neutral')
      face.setEmotion(emotion)
      return { emotion }
    },
  }
}

function pick(args: unknown, key: string): unknown {
  return typeof args === 'object' && args !== null ? (args as Record<string, unknown>)[key] : undefined
}
